import { useEffect, useReducer, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, FormLabel, InputLabel, MenuItem, Select, TextareaAutosize, ThemeProvider, createTheme, } from '@mui/material';
import { showSnackbar } from '../../services/snackbar.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { DecisionEnum, RoleEnum } from '../../types/enums';
import { GetJudgesByRegion } from '../../services/user-service';
import { IJudgeDetail } from '../../types/judge';

const initialState = {
    regionId: null,
    submissionStage: "",
    competencyId: 0,
    judgeUserId: null,
    isAwarded: false,
    decisionDateTime: null,
    judgeFeedback: null,
    judge2UserId: null,
    judge2Feedback: null,
    secondedDateTime: null
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

/**
 * @param props - all of the functions and properties that The judge Dialog requires to allow
 * users to assign judges and make decisions about a submission.
 * @returns - a Dialog which has the judge information for a submission.
 * @remarks Judge Dialog allows a user to assign a judge if they have permissions to
 * and it is also where a judge once assigned, will give feedback and make a decision about the submission.
 */
export function JudgeDialog(props) {
    const { showDialog, selectedSubmission, selectedUser, handleClose, changeJudge, handleSave, judgeDecision, judgeDecision2 } = props;
    const classes = useStyles();
    const user = useSelector((state: RootState) => state.userDetail);
    const [decisionOfJudge, setDecisionOfJudge] = useState(judgeDecision);
    const [decisionOfJudge2, setDecisionOfJudge2] = useState(judgeDecision2);
    const [judgeList, setJudgeList] = useState<IJudgeDetail[]>([]);
    const [judge2List, setJudge2List] = useState<IJudgeDetail[]>([]);
    const maxLength = 300;

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#10AD61'
            },
            body1: {
                color: '#00000'
            }
        },
    });

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        userId: selectedUser?.userId,
        regionId: selectedUser?.regionId,
        competencyId: selectedSubmission.competencyId,
        submissionStage: selectedSubmission?.submissionStage,
        isAwarded: selectedSubmission?.isAwarded,
        submissionId: selectedSubmission?.id,
        judgeUserId: selectedSubmission?.judgeUserId,
        judgeFeedback: selectedSubmission?.judgeFeedback,
        judge2UserId: selectedSubmission?.judge2UserId,
        judge2Feedback: selectedSubmission?.judge2Feedback
    });

    const { submissionStage, judgeUserId, judgeFeedback, judge2UserId, judge2Feedback } = state;

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    }

    const handleSubmit = async () => {
        if (judgeUserId !== selectedSubmission.judgeUserId) {
            await changeJudge(judgeUserId);
        }
        else if (judge2UserId !== selectedSubmission.judge2UserId) {
            await changeJudge(judge2UserId);
        }

        if (judge2UserId === null && decisionOfJudge !== judgeDecision && decisionOfJudge !== 1) {
            await handleSave(judgeFeedback, decisionOfJudge);
        }
        else if (judgeDecision === DecisionEnum.Declined && decisionOfJudge2 !== judgeDecision2 && decisionOfJudge2 !== 1) {
            await handleSave(judge2Feedback, decisionOfJudge2);
        }
    }

    const getJudgeList = async (regionId: number, judge1Id: number, judge2Id?: number) => {
        try {
            let list = await GetJudgesByRegion(regionId, judge1Id, judge2Id);
            list = list.filter((item) => item.id != user.id);
            setJudgeList(list);
            setJudge2List(list.filter((item) => item.roleId <= RoleEnum.CredentialingManager));
        } catch {
            showSnackbar("error getting judges", 'error', 5000);
        }
    };

    const isFormValid = (
        (judgeUserId !== null || judgeUserId !== undefined) &&
        (judgeFeedback !== null || judgeFeedback !== undefined) &&
        (decisionOfJudge !== null || judgeDecision !== undefined) &&
        (decisionOfJudge === DecisionEnum.Declined ? judgeDecision2 !== null || judgeDecision2 !== undefined : true) &&
        (decisionOfJudge === DecisionEnum.Declined ? judge2UserId !== null || judge2UserId !== undefined : true) &&
        (decisionOfJudge === DecisionEnum.Declined ? judge2Feedback !== null || judge2Feedback !== undefined : true)
    )

    useEffect(() => {
        if (user.roleId <= RoleEnum.SchoolCoordinator) {
            getJudgeList(selectedUser?.regionId, selectedSubmission.judgeUserId, selectedSubmission?.judge2UserId);
        }
    }, [selectedSubmission, selectedUser]);

    return (
        <div>
            <ThemeProvider theme={theme}>
                <Dialog
                    open={showDialog}
                    onClose={handleClose}
                    fullWidth
                    maxWidth={'md'}
                >
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar sx={{ justifyContent: 'flex-end' }}>
                            <Typography sx={{ flex: 1 }} variant="h6" component="div">
                                Judge Form
                            </Typography>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <Grid alignItems={"flex-start"} justifyContent={'flex-start'}>
                        <Grid
                            container
                            rowSpacing={2}
                            className={classes.subWrapper3}
                            direction={"column"}
                            justifyContent={'flex-start'}
                            alignItems={"flex-start"}
                        >
                            <Grid container direction={"row"} rowSpacing={4} alignItems={"center"} justifyContent={"center"}>
                                <Grid item xs={10} >
                                    <Grid
                                        container
                                        direction={"row"}
                                        justifyContent="space-around"
                                        alignItems="center"
                                    >
                                        <Grid item xs={4} className={classes.gridPaddingTop}>
                                            <Grid container direction={'column'} rowSpacing={'2%'}>
                                                <Grid item xs={3}>
                                                    <Grid container justifyContent={'center'} alignItems={"center"}>
                                                        <Grid item xs={12}>
                                                            <Typography variant='h6'>
                                                                First Judge
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Judge</FormLabel>
                                                        <Select
                                                            value={judgeUserId}
                                                            disabled={user.roleId > RoleEnum.CredentialingManager ||
                                                                submissionStage === 'Submission Denied by Judge 1—Awaiting Second Judge Assignment' ||
                                                                submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge's Review" ||
                                                                submissionStage === "Submission Approved" ||
                                                                submissionStage === 'Submission Denied by Both Judges'}
                                                            onChange={(event) => setField('judgeUserId', event?.target.value)}
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                        >
                                                            {user.roleId <= RoleEnum.SystemAdministrator && <MenuItem value={user.id}>
                                                                {user.firstName + " " + user.lastName}
                                                            </MenuItem>}
                                                            {judgeList.map((judge) => (
                                                                <MenuItem value={judge.id}>
                                                                    {judge.firstName + " " + judge.lastName}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <InputLabel>Feedback</InputLabel>
                                                    <TextareaAutosize
                                                        value={judgeFeedback}
                                                        disabled={user.id !== judgeUserId ||
                                                            submissionStage === 'Submission Denied by Judge 1—Awaiting Second Judge Assignment' ||
                                                            submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge's Review" ||
                                                            submissionStage === "Submission Approved" ||
                                                            submissionStage === 'Submission Denied by Both Judges'}
                                                        style={{ width: '100%', resize: 'none' }}
                                                        maxLength={maxLength}
                                                        onChange={(event) => setField('judgeFeedback', event?.target.value)}
                                                        minRows={6}
                                                        placeholder="Type your feedback here"
                                                    />
                                                    <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'gray' }}>
                                                        {`${judgeFeedback?.length || 0}/${maxLength}`}
                                                    </div>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Decision</FormLabel>
                                                        <Select
                                                            value={decisionOfJudge}
                                                            disabled={user.id !== judgeUserId ||
                                                                submissionStage === 'Submission Denied by Judge 1—Awaiting Second Judge Assignment' ||
                                                                submissionStage === "Submission Denied by Judge 1—Awaiting Second Judge's Review" ||
                                                                submissionStage === "Submission Approved" ||
                                                                submissionStage === 'Submission Denied by Both Judges'}
                                                            onChange={(event) => setDecisionOfJudge(event.target.value)}
                                                        >
                                                            <MenuItem value={DecisionEnum.Pending}>Pending</MenuItem>
                                                            <MenuItem value={DecisionEnum.Approved}>Approved</MenuItem>
                                                            <MenuItem value={DecisionEnum.Declined}>Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={4} className={classes.gridPaddingTop}>
                                            {(judgeDecision === DecisionEnum.Declined && user.roleId < RoleEnum.Youth) && <Grid container direction={'column'} rowSpacing={'3%'} >
                                                <Grid item xs={3}>
                                                    <Grid container justifyContent={'center'} alignItems={"center"}>
                                                        <Grid item xs={12}>
                                                            <Typography variant='h6'>
                                                                Second Judge
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Judge</FormLabel>
                                                        <Select
                                                            value={judge2UserId}
                                                            disabled={user.roleId > RoleEnum.CredentialingManager ||
                                                                submissionStage === "Submission Approved" ||
                                                                submissionStage === 'Submission Denied by Both Judges'}
                                                            onChange={(event) => setField('judge2UserId', event?.target.value)}
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                        >
                                                            {user.roleId <= RoleEnum.SystemAdministrator && <MenuItem value={user.id}>
                                                                {user.firstName + " " + user.lastName}
                                                            </MenuItem>}
                                                            {judge2List.map((judge) => (
                                                                <MenuItem value={judge.id}>
                                                                    {judge.firstName + " " + judge.lastName}
                                                                </MenuItem>
                                                            ))}

                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <InputLabel>Feedback</InputLabel>
                                                    <TextareaAutosize
                                                        value={judge2Feedback}
                                                        disabled={user.id !== judge2UserId ||
                                                            submissionStage === "Submission Approved" ||
                                                            submissionStage === 'Submission Denied by Both Judges'}
                                                        onChange={(event) => setField('judge2Feedback', event?.target.value)}
                                                        minRows={6}
                                                        placeholder="Type your feedback here"
                                                        style={{ width: '100%', resize: 'none' }}
                                                        maxLength={maxLength}
                                                    />
                                                    <div style={{ textAlign: 'right', fontSize: '0.8rem', color: 'gray' }}>
                                                        {`${judge2Feedback?.length || 0}/${maxLength}`}
                                                    </div>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <FormControl fullWidth>
                                                        <FormLabel>Decision</FormLabel>
                                                        <Select
                                                            value={decisionOfJudge2}
                                                            disabled={user.id !== judge2UserId ||
                                                                submissionStage === "Submission Approved" ||
                                                                submissionStage === 'Submission Denied by Both Judges'}
                                                            onChange={(event) => setDecisionOfJudge2(event.target.value)}
                                                        >
                                                            <MenuItem value={DecisionEnum.Pending}>Pending</MenuItem>
                                                            <MenuItem value={DecisionEnum.Approved}>Approved</MenuItem>
                                                            <MenuItem value={DecisionEnum.Declined}>Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} alignSelf={"center"} className={classes.btnContainer} >
                                <Grid container direction={"column"} rowSpacing={3}>
                                    <Grid item xs={7} md={7}>
                                        <Button onClick={handleSubmit} disabled={!isFormValid} variant="contained" fullWidth>
                                            Update Form
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Dialog >
            </ThemeProvider>
        </div >
    );
}